import type { SeasonLeaderboardEntry } from '@shared/seasons';
import { CROWN_GLYPH, CUP_GLYPH, STAR_GLYPH } from '../../components/currencyGlyphs';
import { leaderboardDisplayName } from '../../components/leaderboardName';

/**
 * The scope's top-N table, ranked by total currency. The signed-in player's
 * row is highlighted; if they rank outside the visible page their row is
 * pinned to the bottom so they can always see their own line.
 */
export function SeasonLeaderboardTable({
  entries,
  me,
  selfUid,
}: {
  entries: SeasonLeaderboardEntry[];
  me: SeasonLeaderboardEntry | null;
  selfUid: string | null;
}) {
  if (entries.length === 0) {
    return (
      <section className="season-board">
        <h2 className="season-section-title">Leaderboard</h2>
        <p className="season-board-empty">
          No-one has earned any currency in this scope yet. Be the first.
        </p>
      </section>
    );
  }

  const meOnPage = me !== null && entries.some((e) => e.userUid === me.userUid);
  const showPinnedMe = me !== null && !meOnPage;

  return (
    <section className="season-board">
      <h2 className="season-section-title">Leaderboard</h2>
      <table className="season-board-table">
        <thead>
          <tr>
            <th className="season-col-rank">#</th>
            <th className="season-col-name">Player</th>
            <th className="season-col-num" title="Stars">
              {STAR_GLYPH}
            </th>
            <th className="season-col-num" title="Cups">
              {CUP_GLYPH}
            </th>
            <th className="season-col-num" title="Crowns">
              {CROWN_GLYPH}
            </th>
            <th className="season-col-num season-col-total" title="Stars + cups + crowns">
              Score
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <SeasonRow key={entry.userUid} entry={entry} isSelf={entry.userUid === selfUid} />
          ))}
          {showPinnedMe && (
            <tr className="season-board-gap">
              <td colSpan={6}>···</td>
            </tr>
          )}
          {showPinnedMe && me && <SeasonRow entry={me} isSelf />}
        </tbody>
      </table>
    </section>
  );
}

function SeasonRow({ entry, isSelf }: { entry: SeasonLeaderboardEntry; isSelf: boolean }) {
  return (
    <tr className={isSelf ? 'season-board-row is-self' : 'season-board-row'}>
      <td className="season-col-rank">{entry.rank.toLocaleString()}</td>
      <td className="season-col-name">
        {leaderboardDisplayName(entry)}
        {isSelf && <span className="season-you-tag">You</span>}
      </td>
      <td className="season-col-num">{entry.stars.toLocaleString()}</td>
      <td className="season-col-num">{entry.cups.toLocaleString()}</td>
      <td className="season-col-num">{entry.crowns.toLocaleString()}</td>
      <td className="season-col-num season-col-total">{entry.total.toLocaleString()}</td>
    </tr>
  );
}
